import React, { useState, useEffect, useCallback } from "react";
import { generateFounders } from "./founderData";
import useSounds from "./hooks/useSounds";
import useSpeechBubble from "./hooks/useSpeechBubble";
import PersonStack from "./components/PersonStack";
import ItemBar from "./components/ItemBar";
import ResumePanel from "./components/ResumePanel";
import BazookaOverlay from "./components/animations/BazookaOverlay";
import SaberOverlay from "./components/animations/SaberOverlay";
import VanOverlay from "./components/animations/VanOverlay";
import GarryOverlay from "./components/animations/GarryOverlay";
import GlockOverlay from "./components/animations/GlockOverlay";
import Tutorial from "./components/Tutorial";
import "./App.css";

const founders = generateFounders(20).sort(() => Math.random() - 0.5);
const STACK_SIZE = 2;

export default function App() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [swipeDir, setSwipeDir] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [bazookaPhase, setBazookaPhase] = useState(null);
  const [saberPhase, setSaberPhase] = useState(null);
  const [vanPhase, setVanPhase] = useState(null);
  const [garryPhase, setGarryPhase] = useState(null);
  const [glockPhase, setGlockPhase] = useState(null);
  const [marcPhase, setMarcPhase] = useState(null);
  const [claudePhase, setClaudePhase] = useState(null); // null | "drop" | "speak" | "run"
  const [showMobileResume, setShowMobileResume] = useState(false);
  const [showTutorial, setShowTutorial] = useState(true);

  const playSound = useSounds();
  const { speechBubble, isSpeaking } = useSpeechBubble(founders, currentIndex, isAnimating);

  const currentFounder = founders[currentIndex % founders.length];

  const stack = [];
  for (let i = STACK_SIZE - 1; i >= 0; i--) {
    const idx = (currentIndex + i) % founders.length;
    stack.push({ founder: founders[idx], depth: i });
  }

  const advance = useCallback(() => {
    setSwipeDir(null);
    setIsAnimating(false);
    setCurrentIndex((i) => i + 1);
  }, []);

  const handleSwipe = useCallback(
    (direction) => {
      if (isAnimating) return;

      if (direction === "right") {
        playSound("yay");
        setSwipeDir(direction);
        setIsAnimating(true);
        return;
      }

      setSwipeDir(direction);
      setIsAnimating(true);

      if (direction === "left") {
        setTimeout(() => playSound("flame"), 800);
      }
    },
    [isAnimating, playSound]
  );

  const handleBazooka = useCallback(() => {
    if (isAnimating || bazookaPhase) return;
    setIsAnimating(true);
    setBazookaPhase("rise");
    setTimeout(() => {
      setBazookaPhase("fire");
      playSound("explosion");
      setTimeout(() => {
        setBazookaPhase("explode");
        setTimeout(() => {
          setBazookaPhase("lower");
          setTimeout(() => {
            setCurrentIndex((i) => i + 1);
            setBazookaPhase(null);
            setSwipeDir(null);
            setIsAnimating(false);
          }, 800);
        }, 1200);
      }, 600);
    }, 800);
  }, [isAnimating, bazookaPhase, playSound]);

  const handleSaber = useCallback(() => {
    if (isAnimating || saberPhase) return;
    setIsAnimating(true);
    setSaberPhase("rise");
    setTimeout(() => {
      setSaberPhase("slash");
      playSound("saber");
      setTimeout(() => {
        setSaberPhase("cut");
        setTimeout(() => {
          setSaberPhase("lower");
          setTimeout(() => {
            setCurrentIndex((i) => i + 1);
            setSaberPhase(null);
            setSwipeDir(null);
            setIsAnimating(false);
          }, 800);
        }, 1000);
      }, 400);
    }, 600);
  }, [isAnimating, saberPhase, playSound]);

  const handleVan = useCallback(() => {
    if (isAnimating || vanPhase) return;
    setIsAnimating(true);
    setVanPhase("enter");
    setTimeout(() => {
      setVanPhase("open");
      setTimeout(() => {
        setVanPhase("grab");
        playSound("scream");
        setTimeout(() => {
          setVanPhase("close");
          setTimeout(() => {
            setVanPhase("drive");
            setTimeout(() => {
              setCurrentIndex((i) => i + 1);
              setVanPhase(null);
              setSwipeDir(null);
              setIsAnimating(false);
            }, 1200);
          }, 400);
        }, 800);
      }, 300);
    }, 1000);
  }, [isAnimating, vanPhase, playSound]);

  const handleGlock = useCallback(() => {
    if (isAnimating || glockPhase) return;
    setIsAnimating(true);
    setGlockPhase("rise");
    // Hand rises up
    setTimeout(() => {
      setGlockPhase("shoot");
      // Fire 6 shots, each with its own sound
      for (let i = 0; i < 6; i++) {
        setTimeout(() => {
          const shot = new Audio("/gunshot.mp3");
          shot.play().catch(() => {});
        }, i * 200);
      }
      // After all shots, play watchasay then lower
      setTimeout(() => {
        playSound("watchasay");
      }, 1200);
      setTimeout(() => {
        setGlockPhase("lower");
        setTimeout(() => {
          setCurrentIndex((i) => i + 1);
          setGlockPhase(null);
          setSwipeDir(null);
          setIsAnimating(false);
        }, 800);
      }, 1500);
    }, 700);
  }, [isAnimating, glockPhase, playSound]);

  const handleMarc = useCallback(() => {
    if (isAnimating || marcPhase) return;
    setIsAnimating(true);
    setMarcPhase("enter");
    // Marc walks in from right
    setTimeout(() => {
      setMarcPhase("pause");
      // Pause, then switch to marchand and grab the guy
      setTimeout(() => {
        setMarcPhase("grab");
        playSound("scream");
        // Drag guy into the abyss
        setTimeout(() => {
          setMarcPhase("exit");
          setTimeout(() => {
            setCurrentIndex((i) => i + 1);
            setMarcPhase(null);
            setSwipeDir(null);
            setIsAnimating(false);
          }, 1200);
        }, 200);
      }, 800);
    }, 1500);
  }, [isAnimating, marcPhase, playSound]);

  const handleClaude = useCallback(() => {
    if (isAnimating || claudePhase) return;
    setIsAnimating(true);
    setClaudePhase("drop");
    // CEO drops from sky
    setTimeout(() => {
      setClaudePhase("speak");
      // Try TTS as best-effort (may not work on iOS Safari)
      const msg = "We at Anthropic have released a new tool that renders your job useless. You are now unemployed.";
      try {
        if (window.speechSynthesis) {
          window.speechSynthesis.cancel();
          const utterance = new SpeechSynthesisUtterance(msg);
          utterance.rate = 1.0;
          utterance.pitch = 0.8;
          utterance.volume = 1;
          window.speechSynthesis.speak(utterance);
        }
      } catch (e) { /* ignore */ }
      // Fixed timeout — don't rely on onend callback
      setTimeout(() => {
        setClaudePhase("run");
        playSound("broplease");
        setTimeout(() => {
          setCurrentIndex((i) => i + 1);
          setClaudePhase(null);
          setSwipeDir(null);
          setIsAnimating(false);
        }, 1500);
      }, 5000);
    }, 1200);
  }, [isAnimating, claudePhase, playSound]);

  const handleGarry = useCallback(() => {
    if (isAnimating || garryPhase) return;
    setIsAnimating(true);
    setGarryPhase("enter");
    setTimeout(() => {
      setGarryPhase("pause");
      setTimeout(() => {
        setGarryPhase("reject");
        playSound("faaah");
        setTimeout(() => {
          setGarryPhase("lead");
          playSound("broplease");
          setTimeout(() => {
            setGarryPhase("exit");
            setTimeout(() => {
              setCurrentIndex((i) => i + 1);
              setGarryPhase(null);
              setSwipeDir(null);
              setIsAnimating(false);
            }, 1200);
          }, 200);
        }, 2500);
      }, 800);
    }, 1800);
  }, [isAnimating, garryPhase, playSound]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") handleSwipe("right");
      if (e.key === "ArrowLeft") handleSwipe("left");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [handleSwipe]);

  // Play paper sound when resume panel appears (isAnimating goes from true to false)
  const prevAnimatingRef = React.useRef(isAnimating);
  useEffect(() => {
    if (prevAnimatingRef.current && !isAnimating) {
      playSound("paper");
    }
    prevAnimatingRef.current = isAnimating;
  }, [isAnimating, playSound]);

  return (
    <div className="page-wrapper">
      <iframe
        className="bg-music"
        id="bg-music-iframe"
        src="https://www.youtube.com/embed/xNjyG8S4_kI?autoplay=1&mute=1&loop=1&playlist=xNjyG8S4_kI&controls=0&showinfo=0&modestbranding=1&enablejsapi=1"
        title="bg-music"
        allow="autoplay"
        frameBorder="0"
        ref={(el) => {
          if (!el) return;
          const unmute = () => {
            el.contentWindow.postMessage(JSON.stringify({
              event: 'command', func: 'unMute', args: []
            }), '*');
            el.contentWindow.postMessage(JSON.stringify({
              event: 'command', func: 'setVolume', args: [50]
            }), '*');
            document.removeEventListener('click', unmute);
            document.removeEventListener('keydown', unmute);
          };
          document.addEventListener('click', unmute);
          document.addEventListener('keydown', unmute);
        }}
      />
      <img src={require('./assets/backedby.png')} alt="Backed by Y Combinator" className="backed-by" draggable={false} />
      <h1 className="site-title">SF Sausage Party</h1>
      <p className="site-subtitle">Fri 8PM · Dolores Park, SF</p>

      <PersonStack
        stack={stack}
        currentIndex={currentIndex}
        currentFounder={currentFounder}
        swipeDir={swipeDir}
        isAnimating={isAnimating}
        isSpeaking={isSpeaking}
        speechBubble={speechBubble}
        bazookaPhase={bazookaPhase}
        saberPhase={saberPhase}
        vanPhase={vanPhase}
        garryPhase={garryPhase}
        glockPhase={glockPhase}
        marcPhase={marcPhase}
        claudePhase={claudePhase}
        advance={advance}
        handleSwipe={handleSwipe}
      />

      <ItemBar
        isAnimating={isAnimating}
        handlers={{ handleBazooka, handleSaber, handleVan, handleGarry, handleGlock, handleMarc, handleClaude }}
      />

      <ResumePanel founder={currentFounder} isAnimating={isAnimating} />

      {/* Mobile Resume Button */}
      <button className="mobile-resume-btn" onClick={() => setShowMobileResume(true)}>
        Resume
      </button>

      {/* Mobile Resume Fullscreen Overlay */}
      {showMobileResume && (
        <div className="mobile-resume-overlay" onClick={() => setShowMobileResume(false)}>
          <div className="mobile-resume-content" onClick={(e) => e.stopPropagation()}>
            <button className="mobile-resume-close" onClick={() => setShowMobileResume(false)}>✕</button>
            <ResumePanel founder={currentFounder} isAnimating={false} />
          </div>
        </div>
      )}

      <SaberOverlay phase={saberPhase} />
      <BazookaOverlay phase={bazookaPhase} />
      <VanOverlay phase={vanPhase} />
      <GarryOverlay phase={garryPhase} />
      <GlockOverlay phase={glockPhase} />

      {/* Marc door + void */}
      {(marcPhase === "grab" || marcPhase === "exit") && (
        <div className="marc-door-wrapper">
          <div className="marc-void" />
          <img src="/door.png" alt="" className="marc-door" draggable={false} />
        </div>
      )}

      {/* Marc Andreessen Animation (page level) */}
      {marcPhase && (
        <img
          src={marcPhase === "grab" || marcPhase === "exit" ? "/marchand.png" : "/marcandersen.png"}
          alt=""
          className={`marc-anim ${marcPhase === "enter" ? "marc-enter" : marcPhase === "pause" ? "marc-paused" : marcPhase === "grab" || marcPhase === "exit" ? "marc-exit" : ""}`}
          draggable={false}
        />
      )}

      {/* Claude CEO Animation (page level) */}
      {claudePhase && (
        <>
          <img
            src="/CEO.png"
            alt=""
            className={`claude-ceo ${claudePhase === "drop" ? "claude-drop" : claudePhase === "speak" ? "claude-landed" : "claude-landed"}`}
            draggable={false}
          />
          {claudePhase === "speak" && (
            <div className="claude-speech-box">
              We at Anthropic have released a new tool that renders your job useless. You are now unemployed.
            </div>
          )}
        </>
      )}

      <a className="credit-tag" href="https://x.com/immike_wing" target="_blank" rel="noopener noreferrer">made by @immike_wing</a>

      {/* Guided Tutorial */}
      {showTutorial && (
        <Tutorial onComplete={() => setShowTutorial(false)} />
      )}
    </div>
  );
}
